// ============================================================
// TMR Portal Admin — modelo de navegação (grupos → itens)
// ============================================================
import type { ReactNode } from 'react';
import { Icons } from './lib/icons';
import { PERMISSIONS } from './data/PermissionsProvider';
import type { PermissionKey } from './data/PermissionsProvider';

export interface NavItem {
  key: string;
  label: string;
  path: string;
  icon: ReactNode;
  /** Permission the operator must hold for this item to show; absent = always visible (skill `frontend-architecture` Regra 5). */
  requires?: PermissionKey;
}

export interface NavGroup {
  group: string;
  items: NavItem[];
}

/**
 * Removes nav items the operator lacks the permission for, and drops any group left empty — so the sidebar
 * shows only what they can reach. Pure, so it is unit-tested without rendering the shell.
 *
 * @param groups The full navigation model.
 * @param has A predicate over the session's permissions (see `useHasPermission`).
 */
export function filterNav(groups: NavGroup[], has: (permission: PermissionKey) => boolean): NavGroup[] {
  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => !item.requires || has(item.requires)) }))
    .filter((group) => group.items.length > 0);
}

/**
 * The nav item a URL belongs to — the one whose path matches whole segments of it, the longest winning.
 * It names the screen in the topbar.
 *
 * A plain prefix test gets two screens wrong, both by list order: `/contratos` claims
 * `/contratos-paciente`, and `/taxas` claims `/taxas/sincronizacao`.
 *
 * @param items The items to search, usually the filtered navigation flattened.
 * @param pathname The current location's path.
 * @returns The item, or undefined when the URL is under none of them (e.g. `/perfil`).
 */
export function findNavItem(items: NavItem[], pathname: string): NavItem | undefined {
  return items
    .filter((item) => pathname === item.path || pathname.startsWith(`${item.path}/`))
    .reduce<NavItem | undefined>((best, item) => (best && best.path.length >= item.path.length ? best : item), undefined);
}

export const NAV: NavGroup[] = [
  {
    group: 'Empresa',
    items: [
      { key: 'companies', label: 'Empresas', path: '/empresas', icon: Icons.building },
      // Right under Empresas, which it feeds: the clinics that asked to join through the site (`28` §9). Its
      // own key, because an application carries personal data of people who are not clients yet.
      {
        key: 'company-applications',
        label: 'Solicitações',
        path: '/empresas/solicitacoes',
        icon: Icons.mail,
        requires: PERMISSIONS.companyApplicationsRead,
      },
      { key: 'contracts', label: 'Contratos', path: '/contratos', icon: Icons.fileText, requires: PERMISSIONS.contractsRead },
      { key: 'parties', label: 'Partes', path: '/partes', icon: Icons.users, requires: PERMISSIONS.partiesRead },
      // A top-level entry, not a tab of an Empresa: this is the TMR's own template, and it has no
      // `produtor_id` to hang it off (15 §5).
      { key: 'global-rates', label: 'Taxas', path: '/taxas', icon: Icons.scale, requires: PERMISSIONS.ratesGlobalRead },
      // Its own entry rather than a tab of /taxas: different subject (what Empresas are charged, not the
      // template) and a different permission, so an operator can hold one without the other.
      { key: 'rate-sync', label: 'Sincronização', path: '/taxas/sincronizacao', icon: Icons.swap, requires: PERMISSIONS.companyRatesAdmin },
      // Right after Taxas, which it mirrors: the TMR's default here, each Empresa's exception on its own tab
      // (`25` §9). Gated by the global key, so an operator who only negotiates exceptions does not see it.
      {
        key: 'business-rules',
        label: 'Regras de Negócio',
        path: '/regras-negocio',
        icon: Icons.settings,
        requires: PERMISSIONS.businessRulesGlobalRead,
      },
    ],
  },
  {
    group: 'Paciente',
    items: [
      // A top-level entry, not a tab of the Empresa: the list is across the whole base, and the operator
      // looking for one usually has a patient or a clinic on the phone (`24` §9).
      {
        key: 'patient-contracts',
        label: 'Contratos de paciente',
        path: '/contratos-paciente',
        icon: Icons.fileText,
        requires: PERMISSIONS.patientContractsRead,
      },
      // Its own entry rather than a tab of Contratos: that one is the Empresa's accreditation, this is the
      // text patients sign, and they are governed by different permissions (`24` §10).
      {
        key: 'contract-templates',
        label: 'Modelos de contrato',
        path: '/modelos-contrato',
        icon: Icons.fileText,
        requires: PERMISSIONS.contractTemplatesRead,
      },
    ],
  },
  {
    // The TMR's credit decisions about the clinics' sales (`26` §9). Its own group: it is about a patient's
    // credit, which is neither the Empresa's record nor a document the patient signs.
    group: 'Crédito',
    items: [
      {
        key: 'manual-reviews',
        label: 'Aprovação manual',
        path: '/credito/aprovacoes',
        icon: Icons.clock,
        requires: PERMISSIONS.creditReviewsRead,
      },
      { key: 'credit-profile', label: 'Consulta', path: '/credito/consulta', icon: Icons.search, requires: PERMISSIONS.creditReviewsRead },
      // The rating's four rules, read and written through Regras de Negócio (`25`), so its gate is that one's.
      { key: 'credit-rules', label: 'Regras', path: '/credito/regras', icon: Icons.settings, requires: PERMISSIONS.businessRulesGlobalRead },
    ],
  },
  {
    // The support workarounds (`30`): one entry for the whole panel, whatever kinds it grows. Its own group
    // because a workaround bypasses a normal step for a patient — none of the other groups' subject.
    group: 'Contingência',
    items: [
      {
        key: 'contingency',
        label: 'Contingência',
        path: '/contingencia',
        icon: Icons.zap,
        requires: PERMISSIONS.contingencyAccess,
      },
    ],
  },
  {
    group: 'Sistema',
    items: [
      { key: 'users', label: 'Usuários', path: '/usuarios', icon: Icons.users, requires: PERMISSIONS.usersManage },
    ],
  },
  {
    group: 'Autorização',
    items: [
      { key: 'roles', label: 'Papéis', path: '/papeis', icon: Icons.shield, requires: PERMISSIONS.authorizationAdmin },
      { key: 'access-requests', label: 'Acessos', path: '/acessos', icon: Icons.mail, requires: PERMISSIONS.authorizationAdmin },
      { key: 'audit-log', label: 'Auditoria', path: '/auditoria', icon: Icons.history, requires: PERMISSIONS.authorizationAdmin },
    ],
  },
];
